import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Scale, FileText, Users, Shield } from 'lucide-react';

const About = () => {
  const { language } = useLanguage();

  return (
    <div className={`min-h-screen bg-adalat-gradient ${language === 'ur' ? 'font-urdu' : 'font-english'}`}>
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-8">
          <Header />
          <LanguageSwitcher />
        </div>

        <main className={`max-w-4xl mx-auto ${language === 'ur' ? 'rtl' : 'ltr'}`}>
          <Card className="p-8 mb-8">
            <h1 className={`${language === 'ur' ? 'text-urdu-3xl text-right' : 'text-4xl'} font-bold mb-6 text-blue-700`}>
              {language === 'ur' ? 'عدالت بوٹ کے بارے میں' : 'About Adalat Bot'}
            </h1>
            
            <div className="space-y-6">
              <p className={`${language === 'ur' ? 'text-urdu-lg text-right' : 'text-lg'} leading-relaxed`}>
                {language === 'ur' 
                  ? 'عدالت بوٹ پاکستان میں قانونی خدمات تک رسائی کو آسان بنانے کے لیے ایک جدید AI پلیٹ فارم ہے۔ ہمارا مشن عام لوگوں کو بااختیار بنانا ہے تاکہ وہ اپنے قانونی حقوق کو سمجھ سکیں اور قانونی دستاویزات آسانی سے تیار کر سکیں۔'
                  : 'Adalat Bot is an advanced AI platform designed to simplify access to legal services in Pakistan. Our mission is to empower ordinary people to understand their legal rights and create legal documents easily.'}
              </p>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <Card className="p-6">
                  <Scale className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-semibold mb-2`}>
                    {language === 'ur' ? 'قانونی رسائی' : 'Legal Access'}
                  </h3>
                  <p className={`${language === 'ur' ? 'text-right' : ''}`}>
                    {language === 'ur'
                      ? 'ہم قانونی خدمات کو ہر کسی کے لیے قابل رسائی اور سستی بناتے ہیں۔ اب آپ کو مہنگے وکیلوں کی ضرورت نہیں صرف بنیادی دستاویزات کے لیے۔'
                      : 'We make legal services accessible and affordable for everyone. No need for expensive lawyers for basic documents.'}
                  </p>
                </Card>

                <Card className="p-6">
                  <FileText className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-semibold mb-2`}>
                    {language === 'ur' ? 'دستاویز کی تیاری' : 'Document Generation'}
                  </h3>
                  <p className={`${language === 'ur' ? 'text-right' : ''}`}>
                    {language === 'ur'
                      ? 'AI کی طاقت کا استعمال کرتے ہوئے، ہم منٹوں میں پیشہ ورانہ قانونی دستاویزات تیار کرتے ہیں جو پاکستانی قوانین کے مطابق ہیں۔'
                      : 'Using AI power, we generate professional legal documents in minutes that comply with Pakistani laws.'}
                  </p>
                </Card>

                <Card className="p-6">
                  <Users className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-semibold mb-2`}>
                    {language === 'ur' ? 'اردو سپورٹ' : 'Urdu Support'}
                  </h3>
                  <p className={`${language === 'ur' ? 'text-right' : ''}`}>
                    {language === 'ur'
                      ? 'ہمارا پلیٹ فارم مکمل طور پر اردو میں دستیاب ہے، جو لاکھوں پاکستانیوں کے لیے قانونی خدمات کو قابل رسائی بناتا ہے۔'
                      : 'Our platform is fully available in Urdu, making legal services accessible to millions of Pakistanis.'}
                  </p>
                </Card>

                <Card className="p-6">
                  <Shield className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-semibold mb-2`}>
                    {language === 'ur' ? 'محفوظ اور نجی' : 'Secure & Private'}
                  </h3>
                  <p className={`${language === 'ur' ? 'text-right' : ''}`}>
                    {language === 'ur'
                      ? 'آپ کا ڈیٹا خفیہ اور محفوظ ہے۔ ہم آپ کی رازداری کو سب سے زیادہ اہمیت دیتے ہیں۔'
                      : 'Your data is encrypted and secure. We prioritize your privacy above all.'}
                  </p>
                </Card>
              </div>

              <div className="mt-8">
                <h2 className={`${language === 'ur' ? 'text-urdu-2xl text-right' : 'text-2xl'} font-bold mb-4 text-blue-700`}>
                  {language === 'ur' ? 'ہم کیسے کام کرتے ہیں' : 'How We Work'}
                </h2>
                <ol className={`space-y-4 ${language === 'ur' ? 'text-right' : ''}`}>
                  <li className={`${language === 'ur' ? 'text-urdu-lg' : 'text-lg'}`}>
                    <strong>{language === 'ur' ? '۱. اپنا مسئلہ بیان کریں:' : '1. Describe Your Issue:'}</strong>
                    {' '}
                    {language === 'ur'
                      ? 'آسان اردو یا انگریزی میں اپنا قانونی مسئلہ بیان کریں۔'
                      : 'Explain your legal issue in simple Urdu or English.'}
                  </li>
                  <li className={`${language === 'ur' ? 'text-urdu-lg' : 'text-lg'}`}>
                    <strong>{language === 'ur' ? '۲. AI پروسیسنگ:' : '2. AI Processing:'}</strong>
                    {' '}
                    {language === 'ur'
                      ? 'ہمارا AI آپ کی معلومات کا تجزیہ کرتا ہے اور پاکستانی قانون کے مطابق دستاویز تیار کرتا ہے۔'
                      : 'Our AI analyzes your information and generates a document according to Pakistani law.'}
                  </li>
                  <li className={`${language === 'ur' ? 'text-urdu-lg' : 'text-lg'}`}>
                    <strong>{language === 'ur' ? '۳. جائزہ اور ترمیم:' : '3. Review & Edit:'}</strong>
                    {' '}
                    {language === 'ur'
                      ? 'تیار شدہ دستاویز کا جائزہ لیں اور ضرورت کے مطابق تبدیلیاں کریں۔'
                      : 'Review the generated document and make changes as needed.'}
                  </li>
                  <li className={`${language === 'ur' ? 'text-urdu-lg' : 'text-lg'}`}>
                    <strong>{language === 'ur' ? '۴. ڈاؤن لوڈ اور استعمال:' : '4. Download & Use:'}</strong>
                    {' '}
                    {language === 'ur'
                      ? 'اپنی دستاویز PDF فارمیٹ میں محفوظ کریں اور استعمال کریں۔'
                      : 'Save your document in PDF format and use it.'}
                  </li>
                </ol>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                <h2 className={`${language === 'ur' ? 'text-urdu-2xl text-right' : 'text-2xl'} font-bold mb-4 text-blue-700`}>
                  {language === 'ur' ? 'اہم نوٹ' : 'Important Note'}
                </h2>
                <p className={`${language === 'ur' ? 'text-urdu-lg text-right' : 'text-lg'} text-gray-700`}>
                  {language === 'ur'
                    ? 'عدالت بوٹ قانونی مشورے کا متبادل نہیں ہے۔ پیچیدہ قانونی معاملات کے لیے ہمیشہ ایک مجاز وکیل سے مشورہ کریں۔ یہ پلیٹ فارم صرف بنیادی قانونی دستاویزات کی تیاری میں مدد کے لیے ہے۔'
                    : 'Adalat Bot is not a substitute for legal advice. Always consult a licensed lawyer for complex legal matters. This platform is only for assistance in preparing basic legal documents.'}
                </p>
              </div>
            </div>
          </Card>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default About;